/**
 * How many voice leads the case has open: recording detections and pairs of similar voiceprints
 * that wait for a decision or for a merge. The server answers counts and names no
 * person, voiceprint or pair, so the read is not audited and is polled like the tattoo count, for
 * the Entity Resolution tab and the case rail. Comparing voices is HUMINT's: with the extension
 * off the count is never read. The rail loads this with the desktop app, so it imports nothing of
 * the HUMINT workspace.
 */
import { useQuery } from '@tanstack/react-query'
import {
  leadCountPoll,
  type TattooLeadCount,
  toLeadCount,
} from '@/features/tattoo-leads/hooks/useTattooLeadCount'
import { HUMINT_EXTENSION_ID, useExtensionEnabled } from '@/hooks/use-extensions'
import {
  humintVoiceMatchingApi,
  type VoiceLeadCountDto,
} from '@/services/api/humint-voice-matching'

/** Under the case's voice reads: the comparison's own reads sit beside these (`voicePairKeys`). */
export const voiceLeadKeys = {
  all: (caseId: string) => ['case', caseId, 'humint', 'voice', 'leads'] as const,
  list: (caseId: string) => [...voiceLeadKeys.all(caseId), 'list'] as const,
  count: (caseId: string) => [...voiceLeadKeys.all(caseId), 'count'] as const,
  recordings: (caseId: string) => [...voiceLeadKeys.all(caseId), 'recordings'] as const,
}

/** The leads to review, the ones waiting for a merge, and their sum: the number a badge shows. */
export type VoiceLeadCount = TattooLeadCount

const POLL_MS = 60 * 1000

/** Recording detections join the review count; only pairs of persons can await a merge. */
export function toVoiceLeadCount(dto: VoiceLeadCountDto | undefined): VoiceLeadCount {
  const pairCount = toLeadCount(dto)
  const toReview = pairCount.toReview + (dto?.recording_to_review ?? 0)
  return { toReview, toMerge: pairCount.toMerge, total: toReview + pairCount.toMerge }
}

/** The count's query: its key, its read and its poll; `humint` false and it is never read. */
export function voiceLeadCountQuery(caseId: string, humint: boolean) {
  return {
    queryKey: voiceLeadKeys.count(caseId),
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      humintVoiceMatchingApi.leadsCount(caseId, signal),
    enabled: humint && Boolean(caseId),
    staleTime: POLL_MS,
    // Comparing runs in the background; the next poll is the retry of a failed one, and a refusal
    // the same request would get again ends it.
    refetchInterval: (query: { state: { error: unknown } }) => leadCountPoll(query.state.error),
    retry: false,
  }
}

/** The case's open voice leads, shared by the rail and the ER tab; null while HUMINT is off. */
export function useVoiceLeadCount(caseId: string): VoiceLeadCount | null {
  const { enabled } = useExtensionEnabled(HUMINT_EXTENSION_ID)
  const { data } = useQuery(voiceLeadCountQuery(caseId, enabled))
  return enabled ? toVoiceLeadCount(data) : null
}
